import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { baseUrl, seo, services } from "@/lib/content";
import { priceForSeo } from "@/lib/currency";
import { getLanguageFromPath } from "@/lib/language";

function upsertMeta(selector: string, attr: "name" | "property", key: string, content: string) {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let element = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement("link");
    element.rel = rel;
    if (hreflang) element.hreflang = hreflang;
    document.head.appendChild(element);
  }
  element.href = href;
}

function upsertJsonLd(id: string, data: unknown) {
  let element = document.getElementById(id) as HTMLScriptElement | null;
  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
}

export default function SeoManager() {
  const location = useLocation();
  const language = getLanguageFromPath(location.pathname);
  const copy = seo[language];
  const currentUrl = `${baseUrl}${language === "en" ? "/en" : "/"}`;

  useEffect(() => {
    document.documentElement.lang = language;
    document.title = copy.title;

    upsertMeta('meta[name="description"]', "name", "description", copy.description);
    upsertMeta('meta[name="keywords"]', "name", "keywords", copy.keywords);
    upsertMeta('meta[property="og:title"]', "property", "og:title", copy.ogTitle);
    upsertMeta('meta[property="og:description"]', "property", "og:description", copy.ogDescription);
    upsertMeta('meta[property="og:url"]', "property", "og:url", currentUrl);
    upsertMeta('meta[property="og:locale"]', "property", "og:locale", copy.locale);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", copy.ogTitle);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", copy.ogDescription);

    upsertLink("canonical", currentUrl);
    upsertLink("alternate", `${baseUrl}/`, "hu");
    upsertLink("alternate", `${baseUrl}/en`, "en");
    upsertLink("alternate", `${baseUrl}/`, "x-default");

    const pricedItems = services[language].categories.flatMap((category) => category.items).slice(0, 4);
    upsertJsonLd("ivision-service-jsonld", {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "iVision Design",
      description: copy.description,
      url: currentUrl,
      email: "workivett@gmail.com",
      telephone: "+36702858571",
      founder: {
        "@type": "Person",
        name: "Lakatos Ivett",
        jobTitle: "Creative Designer",
        sameAs: [
          "https://www.behance.net/iVision",
          "https://www.linkedin.com/in/ivett-lakatos-42a7852bb/",
          "https://www.instagram.com/ivision_design/",
        ],
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: copy.catalogName,
        itemListElement: pricedItems.map((item) => {
          const price = priceForSeo(item.huf, language);
          return {
            "@type": "Offer",
            price: price.price,
            priceCurrency: price.currency,
            itemOffered: {
              "@type": "Service",
              name: item.name,
              description: item.detail,
            },
          };
        }),
      },
    });

    upsertJsonLd("ivision-faq-jsonld", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: copy.faq.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    });
  }, [copy, currentUrl, language]);

  return null;
}
